import { useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/components/feature/Toast";
import SmartMedia from "@/components/feature/SmartMedia";
import ConfirmDialog from "@/pages/admin/dashboard/components/ConfirmDialog";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import ProductPreviewModal from "@/pages/admin/dashboard/components/ProductPreviewModal";
import {
  formatPrice,
  getProductImages,
  timeAgo,
} from "@/pages/admin/dashboard/types";
import type { AdminProduct, AdminProfile } from "@/pages/admin/dashboard/types";

interface ModerationTabProps {
  products: AdminProduct[];
  profiles: AdminProfile[];
  onRefresh: () => void;
}

export default function ModerationTab({
  products,
  profiles,
  onRefresh,
}: ModerationTabProps) {
  const { showToast } = useToast();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AdminProduct | null>(null);
  const [previewTarget, setPreviewTarget] = useState<AdminProduct | null>(null);

  const sellerNameById = useMemo(() => {
    const map = new Map<string, string>();
    profiles.forEach((p) =>
      map.set(p.id, p.full_name ?? p.email ?? "Vendedor")
    );
    return map;
  }, [profiles]);

  const handleApprove = async (product: AdminProduct) => {
    setBusyId(product.id);
    const { error } = await supabase
      .from("products")
      .update({ status: "active" })
      .eq("id", product.id);
    setBusyId(null);
    if (error) {
      showToast("No se pudo aprobar la publicación.", "error");
      return;
    }
    setPreviewTarget(null);
    showToast(`"${product.title}" fue aprobada.`, "success");
    onRefresh();
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setBusyId(rejectTarget.id);
    const { error } = await supabase
      .from("products")
      .update({ status: "deleted" })
      .eq("id", rejectTarget.id);
    setBusyId(null);
    const target = rejectTarget;
    setRejectTarget(null);
    setPreviewTarget(null);
    if (error) {
      showToast("No se pudo rechazar la publicación.", "error");
      return;
    }
    showToast(`"${target.title}" fue rechazada.`, "success");
    onRefresh();
  };

  if (products.length === 0) {
    return (
      <EmptyState
        icon="ri-check-double-line"
        title="Sin publicaciones pendientes"
        description="No hay productos en espera de moderación. ¡Todo al día!"
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] text-left">
        <thead>
          <tr className="border-b border-background-200/70 bg-background-100/60 text-xs font-semibold uppercase tracking-wide text-foreground-500">
            <th className="px-5 py-3">Publicación</th>
            <th className="px-5 py-3">Vendedor</th>
            <th className="px-5 py-3">Precio</th>
            <th className="px-5 py-3">Recibida</th>
            <th className="px-5 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {products.map((product) => {
            const images = getProductImages(product);
            const cover = images[0] ?? null;
            return (
              <tr
                key={product.id}
                className="border-b border-background-200/60 transition-colors last:border-0 hover:bg-background-100/40"
              >
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    {cover ? (
                      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-md border border-background-200">
                        <SmartMedia
                          src={cover}
                          alt={product.title}
                          className="h-full w-full object-cover object-top"
                          videoControls={false}
                        />
                      </div>
                    ) : (
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-background-100">
                        <i className="ri-image-line text-lg text-foreground-400"></i>
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="max-w-[260px] truncate text-sm font-semibold text-foreground-950">
                        {product.title}
                      </p>
                      <p className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                        <span>{product.category ?? "Sin categoría"}</span>
                        <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                        <span className="capitalize">{product.condition}</span>
                        {images.length > 1 && (
                          <>
                            <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                            <span className="inline-flex items-center gap-0.5">
                              <i className="ri-image-line"></i>
                              {images.length}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-foreground-700">
                  {sellerNameById.get(product.seller_id) ?? "Vendedor"}
                </td>
                <td className="px-5 py-3 text-sm font-semibold text-foreground-900">
                  {formatPrice(product.price, product.currency)}
                </td>
                <td className="px-5 py-3 text-sm text-foreground-600">
                  {timeAgo(product.created_at)}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setPreviewTarget(product)}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                      aria-label={`Ver vista previa de ${product.title}`}
                    >
                      <i className="ri-eye-line"></i>
                      Vista previa
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApprove(product)}
                      disabled={busyId === product.id}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {busyId === product.id ? (
                        <i className="ri-loader-4-line animate-spin"></i>
                      ) : (
                        <i className="ri-check-line"></i>
                      )}
                      Aprobar
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejectTarget(product)}
                      disabled={busyId === product.id}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-200 bg-accent-50 px-3 py-1.5 text-xs font-semibold text-accent-700 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <i className="ri-delete-bin-line"></i>
                      Rechazar
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <ProductPreviewModal
        product={previewTarget}
        sellerName={
          previewTarget
            ? sellerNameById.get(previewTarget.seller_id) ?? "Vendedor"
            : "Vendedor"
        }
        busy={busyId !== null}
        onApprove={handleApprove}
        onReject={(product) => setRejectTarget(product)}
        onClose={() => setPreviewTarget(null)}
      />

      <ConfirmDialog
        open={rejectTarget !== null}
        title="¿Rechazar esta publicación?"
        description={`"${rejectTarget?.title ?? ""}" se marcará como eliminada y dejará de ser visible para los compradores.`}
        confirmLabel="Rechazar"
        tone="danger"
        loading={busyId === rejectTarget?.id}
        onConfirm={handleReject}
        onCancel={() => setRejectTarget(null)}
      />
    </div>
  );
}